import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	ChevronLeft,
	Mail,
	MoreHorizontal,
	Check,
	RotateCcw,
	ExternalLink,
	Copy,
	UserX,
	Flag,
	Trash2,
	ChevronDown,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import {
	useMarkInquiryRead,
	useToggleInquiryHandled,
	useDeleteInquiry,
	useBlockInquirySender,
	useReportInquiry,
	useSetInquiryStatus,
	useInquiryReplies,
	useSendInquiryReply,
	type Inquiry,
	type InquiryStatus,
} from '@/hooks/useInquiries';
import { ChatComposer } from './ChatComposer';
import { formatUsdCents } from '@/lib/formatMoney';

interface InquiryThreadProps {
	inquiry: Inquiry;
	onBack: () => void;
}

const fmtMoney = (cents: number | null | undefined) =>
	cents == null ? null : `${formatUsdCents(cents)}`;

/** Gravatar (SHA-256) — works for any email, no account required. d=404 lets us detect missing. */
async function sha256Hex(input: string): Promise<string> {
	const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
	return Array.from(new Uint8Array(buf))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');
}

function useGravatar(email: string) {
	const [url, setUrl] = useState<string | null>(null);
	useEffect(() => {
		let cancel = false;
		const normalized = (email || '').trim().toLowerCase();
		if (!normalized) {
			setUrl(null);
			return;
		}
		sha256Hex(normalized).then((hash) => {
			if (!cancel) setUrl(`https://www.gravatar.com/avatar/${hash}?s=128&d=404`);
		});
		return () => {
			cancel = true;
		};
	}, [email]);
	return url;
}

function StatusDot({ status }: { status: InquiryStatus }) {
	return (
		<span
			className={cn(
				'h-2 w-2 rounded-full',
				status === 'won'
					? 'bg-success'
					: status === 'lost'
						? 'bg-white/30'
						: 'bg-active-gold'
			)}
		/>
	);
}

function ReplyAttachment({ path }: { path: string }) {
	const [open, setOpen] = useState(false);
	const [url, setUrl] = useState<string>('');
	useEffect(() => {
		let cancelled = false;
		supabase.storage
			.from('inquiry-attachments')
			.createSignedUrl(path, 60 * 60)
			.then(({ data }) => {
				if (!cancelled) setUrl(data?.signedUrl ?? '');
			});
		return () => {
			cancelled = true;
		};
	}, [path]);
	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="h-16 w-16 rounded-xl overflow-hidden bg-white/[0.05] border border-white/[0.08] shrink-0"
			>
				{url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
			</button>
			{open && (
				<button
					type="button"
					onClick={() => setOpen(false)}
					className="fixed inset-0 z-[80] bg-black/80 grid place-items-center p-4"
				>
					<img src={url} alt="" className="max-h-full max-w-full rounded-lg" />
				</button>
			)}
		</>
	);
}

/** Pinned listing-context card. Renders only when inquiry.item_id is set and load succeeds. */
function InquiryListingCard({ itemId }: { itemId: string }) {
	const { data } = useQuery({
		queryKey: ['inquiry-listing', itemId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('user_items')
				.select('id, short_id, title, name, asking_price_cents, primary_image_url, condition, set_name')
				.eq('id', itemId)
				.maybeSingle();
			if (error) throw error;
			return data;
		},
	});

	if (!data) return null;
	const title = (data as any).title || (data as any).name || 'Listing';
	const price = fmtMoney((data as any).asking_price_cents);
	const img = (data as any).primary_image_url as string | null;
	const setName = (data as any).set_name as string | null;
	const condition = (data as any).condition as string | null;
	const href = `/item/${(data as any).short_id || (data as any).id}`;

	return (
		<Link
			to={href}
			className="flex gap-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.06] border border-white/[0.06] p-3 transition-colors"
		>
			<div className="h-20 w-20 rounded-xl overflow-hidden bg-white/[0.05] shrink-0 grid place-items-center">
				{img ? (
					<img src={img} alt="" className="h-full w-full object-cover" />
				) : (
					<Mail className="h-6 w-6 text-muted-foreground/40" />
				)}
			</div>
			<div className="min-w-0 flex-1 flex flex-col justify-between py-0.5">
				<div className="min-w-0">
					<div className="text-[10px] font-bold uppercase tracking-widest text-active-gold/80 leading-none mb-1">
						Inquiry about
					</div>
					<div className="text-[14px] text-foreground font-semibold truncate leading-tight">
						{title}
					</div>
					{(setName || condition) && (
						<div className="text-[11px] text-muted-foreground truncate mt-0.5">
							{[setName, condition?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}
						</div>
					)}
				</div>
				<div className="flex items-center justify-between gap-2 mt-1.5">
					{price && (
						<div className="text-[13px] text-active-gold font-semibold leading-none">
							{price}
						</div>
					)}
					<div className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
						View <ExternalLink className="h-3 w-3" />
					</div>
				</div>
			</div>
		</Link>
	);
}

/**
 * Read-only chat-style view of a guest inquiry.
 * Guests have no account — sole reply path is mailto: from the seller's mail client.
 */
export const InquiryThread = ({ inquiry, onBack }: InquiryThreadProps) => {
	const markRead = useMarkInquiryRead();
	const toggleHandled = useToggleInquiryHandled();
	const deleteInquiry = useDeleteInquiry();
	const blockSender = useBlockInquirySender();
	const reportInquiry = useReportInquiry();
	const setStatus = useSetInquiryStatus();
	const [confirm, setConfirm] = useState<null | 'delete' | 'block' | 'report'>(null);
	const status: InquiryStatus = inquiry.status ?? 'waiting';
	const [wonInput, setWonInput] = useState<string>(
		inquiry.won_amount_cents != null
			? (inquiry.won_amount_cents / 100).toFixed(2)
			: inquiry.offer_cents != null
				? (inquiry.offer_cents / 100).toFixed(2)
				: '',
	);
	const [lostInput, setLostInput] = useState<string>(inquiry.lost_reason ?? '');
	const [pendingStatus, setPendingStatus] = useState<null | 'won' | 'lost'>(null);
	const [expandedPipeline, setExpandedPipeline] = useState(false);

	const applyStatus = (next: InquiryStatus) => {
		if (inquiry._table !== 'inquiries') {
			toast.error('Legacy inquiries can\'t be tracked — reply directly.');
			return;
		}
		if (next === status && pendingStatus === null) return;
		if (next === 'waiting') {
			setPendingStatus(null);
			setStatus.mutate(
				{ id: inquiry.id, status: 'waiting', table: inquiry._table },
				{ onSuccess: () => toast.success('Moved back to Waiting') },
			);
			return;
		}
		setPendingStatus(next);
	};

	const commitWon = () => {
		const cents = wonInput.trim()
			? Math.round(parseFloat(wonInput.replace(/[^0-9.]/g, '')) * 100)
			: null;
		setStatus.mutate(
			{
				id: inquiry.id,
				status: 'won',
				wonAmountCents: cents && Number.isFinite(cents) && cents > 0 ? cents : null,
				table: inquiry._table,
			},
			{
				onSuccess: () => {
					toast.success('Marked as won');
					setPendingStatus(null);
				},
				onError: (e: any) => toast.error(e?.message || 'Could not save'),
			},
		);
	};

	const commitLost = (reason?: string) => {
		setStatus.mutate(
			{
				id: inquiry.id,
				status: 'lost',
				lostReason: (reason ?? lostInput).trim() || null,
				table: inquiry._table,
			},
			{
				onSuccess: () => {
					toast.success('Marked as lost');
					setPendingStatus(null);
				},
				onError: (e: any) => toast.error(e?.message || 'Could not save'),
			},
		);
	};


	useEffect(() => {
		if (!inquiry.read_at) markRead.mutate({ id: inquiry.id, table: inquiry._table });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inquiry.id]);

	// Lift listing query so menu can navigate / link.
	const { data: listing } = useQuery({
		queryKey: ['inquiry-listing', inquiry.item_id],
		enabled: !!inquiry.item_id,
		queryFn: async () => {
			const { data, error } = await supabase
				.from('user_items')
				.select('id, short_id, title, name, asking_price_cents, primary_image_url')
				.eq('id', inquiry.item_id as string)
				.maybeSingle();
			if (error) throw error;
			return data;
		},
	});
	const listingHref = listing
		? `/item/${(listing as any).short_id || (listing as any).id}`
		: null;

	const offer = fmtMoney(inquiry.offer_cents);
	const displayName = inquiry.guest_name || inquiry.guest_email;
	const initial = (displayName || '?').charAt(0).toUpperCase();
	const gravatar = useGravatar(inquiry.guest_email);
	const [avatarFailed, setAvatarFailed] = useState(false);
	const isHandled = !!inquiry.handled_at;

	// Native reply composer state
	const [draft, setDraft] = useState('');
	const composerRef = useRef<HTMLTextAreaElement | null>(null);
	const sendReply = useSendInquiryReply();
	const { data: replies = [] } = useInquiryReplies(inquiry.id, inquiry._table);

	const focusComposer = () => {
		requestAnimationFrame(() => composerRef.current?.focus());
	};

	const submitReply = () => {
		const body = draft.trim();
		if (!body || sendReply.isPending) return;
		sendReply.mutate(
			{ inquiryId: inquiry.id, inquiryTable: inquiry._table, body },
			{
				onSuccess: (res) => {
					setDraft('');
					toast.success(res?.emailQueued === false ? 'Reply saved (email pending)' : 'Reply sent');
				},
				onError: (e: any) => toast.error(e?.message || 'Could not send reply'),
			},
		);
	};

	const mailtoFallback = `mailto:${encodeURIComponent(inquiry.guest_email)}?subject=${encodeURIComponent(inquiry.item_id ? 'Re: your CardSimp listing' : 'Re: your CardSimp store')}`;

	const copy = async (text: string, label: string) => {
		try {
			await navigator.clipboard.writeText(text);
			toast.success(`${label} copied`);
		} catch {
			toast.error('Copy failed');
		}
	};

	const onBlock = () => {
		blockSender.mutate(
			{ email: inquiry.guest_email, inquiryId: inquiry.id, table: inquiry._table },
			{
				onSuccess: () => {
					toast.success(`Blocked ${inquiry.guest_email}`);
					onBack();
				},
				onError: (e: any) => toast.error(e?.message || 'Block failed'),
			},
		);
	};

	const onReport = () => {
		reportInquiry.mutate(inquiry, {
			onSuccess: () => {
				toast.success('Reported to CardSimp');
				toggleHandled.mutate({ id: inquiry.id, handled: true, table: inquiry._table });
				onBack();
			},
			onError: (e: any) => toast.error(e?.message || 'Report failed'),
		});
	};

	const onDelete = () => {
		deleteInquiry.mutate(
			{ id: inquiry.id, table: inquiry._table },
			{
				onSuccess: () => {
					toast.success('Thread deleted');
					onBack();
				},
				onError: (e: any) => toast.error(e?.message || 'Delete failed'),
			},
		);
	};

	const confirmBusy =
		reportInquiry.isPending || blockSender.isPending || deleteInquiry.isPending;


	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="px-3 pt-2.5 pb-2.5 md:pr-[76px] flex items-center gap-2 shrink-0 border-b border-white/[0.05]">
				<button
					type="button"
					onClick={onBack}
					aria-label="Back to inbox"
					className="h-8 w-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors -ml-1"
				>
					<ChevronLeft className="h-5 w-5" />
				</button>
				<div
					className="h-9 w-9 rounded-full grid place-items-center shrink-0 overflow-hidden bg-active-gold/15 text-active-gold text-[13px] font-semibold"
					style={{ boxShadow: '0 0 0 1px hsl(var(--active-gold) / 0.3)' }}
				>
					{gravatar && !avatarFailed ? (
						<img
							src={gravatar}
							alt=""
							className="h-full w-full object-cover"
							onError={() => setAvatarFailed(true)}
						/>
					) : (
						initial
					)}
				</div>
				<div className="min-w-0 flex-1">
					<div className="text-[13px] font-semibold text-foreground leading-tight truncate">
						{displayName}
					</div>
					{inquiry.guest_name && (
						<div className="text-[11px] text-muted-foreground leading-tight truncate">
							{inquiry.guest_email}
						</div>
					)}
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							aria-label="Thread options"
							className="h-8 w-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors"
						>
							<MoreHorizontal className="h-5 w-5" />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="z-[70] w-56">
						<DropdownMenuItem
							onSelect={() =>
								toggleHandled.mutate({ id: inquiry.id, handled: !isHandled, table: inquiry._table })
							}
						>
							{isHandled ? (
								<>
									<RotateCcw className="w-4 h-4 mr-2" />
									Move back to inbox
								</>
							) : (
								<>
									<Check className="w-4 h-4 mr-2" />
									Mark as done
								</>
							)}
						</DropdownMenuItem>
						{listingHref && (
							<DropdownMenuItem asChild>
								<Link to={listingHref}>
									<ExternalLink className="w-4 h-4 mr-2" />
									View listing
								</Link>
							</DropdownMenuItem>
						)}
						<DropdownMenuItem onSelect={() => copy(inquiry.guest_email, 'Email')}>
							<Copy className="w-4 h-4 mr-2" />
							Copy email
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<a href={mailtoFallback}>
								<Mail className="w-4 h-4 mr-2" />
								Open in mail app
							</a>
						</DropdownMenuItem>

						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={() => setConfirm('block')}>
							<UserX className="w-4 h-4 mr-2" />
							Block sender
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => setConfirm('report')}
							className="text-destructive focus:text-destructive"
						>
							<Flag className="w-4 h-4 mr-2" />
							Report spam
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => setConfirm('delete')}
							className="text-destructive focus:text-destructive"
						>
							<Trash2 className="w-4 h-4 mr-2" />
							Delete thread
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{/* Pipeline status — compact header pill that expands on click */}
			{inquiry._table === 'inquiries' && (
				<div className="shrink-0 px-3 pt-2 pb-2 border-b border-white/[0.05]">
					<button
						type="button"
						onClick={() => setExpandedPipeline((v) => !v)}
						className="w-full flex items-center justify-between gap-2 rounded-xl bg-black/40 border border-white/[0.06] px-3 py-2 text-left"
					>
						<div className="flex items-center gap-2">
							<StatusDot status={status} />
							<span className="text-[12px] font-semibold text-foreground capitalize">{status}</span>
							{status === 'won' && inquiry.won_amount_cents != null && (
								<span className="text-[11px] text-success">
									{formatUsdCents(inquiry.won_amount_cents)}
								</span>
							)}
							{status === 'lost' && inquiry.lost_reason && (
								<span className="text-[11px] text-muted-foreground truncate max-w-[140px]">
									{inquiry.lost_reason}
								</span>
							)}
						</div>
						<ChevronDown
							className={cn(
								'h-4 w-4 text-muted-foreground transition-transform',
								expandedPipeline && 'rotate-180'
							)}
						/>
					</button>
					{expandedPipeline && (
						<div className="mt-2 space-y-2">
							<div className="grid grid-cols-3 gap-1 p-1 rounded-full bg-black/40 border border-white/[0.06]">
								{(['waiting', 'won', 'lost'] as const).map((s) => {
									const active = status === s && pendingStatus === null;
									return (
										<button
											key={s}
											type="button"
											onClick={() => applyStatus(s)}
											disabled={setStatus.isPending}
											className={cn(
												'h-8 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors',
												active
													? s === 'won'
														? 'bg-success text-background'
														: s === 'lost'
															? 'bg-white/[0.08] text-foreground'
															: 'bg-active-gold text-[#121416]'
													: 'text-muted-foreground hover:text-foreground',
											)}
										>
											{s}
										</button>
									);
								})}
							</div>
							{pendingStatus === 'won' && (
								<div className="flex items-center gap-2 rounded-xl bg-success/10 border border-success/30 px-2.5 py-2">
									<span className="text-[11px] uppercase tracking-wider text-success/80 font-semibold">Sold for</span>
									<div className="relative flex-1">
										<span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
										<input
											type="text"
											inputMode="decimal"
											pattern="[0-9]*\.?[0-9]*"
											name="price"
											autoComplete="off"
											autoCorrect="off"
											autoCapitalize="off"
											spellCheck={false}
											data-1p-ignore
											data-lpignore="true"
											value={wonInput}
											onChange={(e) => setWonInput(e.target.value.replace(/[^0-9.]/g, ''))}
											placeholder="0.00"
											className="w-full h-9 pl-6 pr-2 rounded-md bg-black/40 border border-white/10 text-sm text-foreground focus:outline-none focus:border-success/60"
										/>
									</div>
									<Button
										size="sm"
										onClick={commitWon}
										disabled={setStatus.isPending}
										className="h-9 rounded-full bg-success text-background hover:bg-success/90"
									>
										Save
									</Button>
									<button
										type="button"
										onClick={() => setPendingStatus(null)}
										className="text-[11px] text-muted-foreground hover:text-foreground px-1"
									>
										Cancel
									</button>
								</div>
							)}
							{pendingStatus === 'lost' && (
								<div className="space-y-1.5 rounded-xl bg-white/[0.03] border border-white/[0.06] px-2.5 py-2">
									<div className="flex flex-wrap gap-1.5">
										{['Sold elsewhere', 'No response', 'Price too low', 'Other'].map((r) => (
											<button
												key={r}
												type="button"
												onClick={() => commitLost(r)}
												disabled={setStatus.isPending}
												className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.05] hover:bg-white/[0.1] text-foreground/80 hover:text-foreground transition-colors"
											>
												{r}
											</button>
										))}
										</div>
										<div className="flex items-center gap-2">
											<input
												type="text"
												value={lostInput}
												onChange={(e) => setLostInput(e.target.value)}
												placeholder="Custom reason (optional)"
												className="flex-1 h-8 px-2.5 rounded-md bg-black/40 border border-white/10 text-[13px] text-foreground focus:outline-none focus:border-white/30"
											/>
											<Button
												size="sm"
												variant="outline"
												onClick={() => commitLost()}
												disabled={setStatus.isPending}
												className="h-8 rounded-full"
											>
												Save
											</Button>
											<button
												type="button"
												onClick={() => setPendingStatus(null)}
												className="text-[11px] text-muted-foreground hover:text-foreground px-1"
											>
												Cancel
											</button>
										</div>
									</div>
								)}
						</div>
					)}
				</div>
			)}

			{/* Body */}
			<div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
				{inquiry.item_id && <InquiryListingCard itemId={inquiry.item_id} />}
				<div className="flex justify-start items-end gap-2">
					<div
						className="h-7 w-7 rounded-full grid place-items-center shrink-0 overflow-hidden bg-active-gold/15 text-active-gold text-[11px] font-semibold self-end"
						aria-hidden
					>
						{gravatar && !avatarFailed ? (
							<img src={gravatar} alt="" className="h-full w-full object-cover" />
						) : (
							initial
						)}
					</div>
					<div className="relative max-w-[82%] rounded-2xl bg-white/[0.06]">
						{offer && (
							<div className="relative flex items-baseline gap-1.5 px-4 pt-2.5 pb-2 border-b border-white/[0.06] bg-active-gold/[0.06] rounded-t-2xl">
								<span className="text-[10px] uppercase tracking-wider text-active-gold/80 font-semibold">
									Offer
								</span>
								<span className="text-[15px] font-semibold text-active-gold leading-none">
									{offer}
								</span>
							</div>
						)}
						<div className="relative px-4 py-2.5">
							<p className="text-[14px] text-foreground leading-snug whitespace-pre-wrap break-words">
								{inquiry.message}
							</p>
							<p className="text-[10.5px] text-muted-foreground mt-1.5">
								{formatDistanceToNow(new Date(inquiry.created_at), { addSuffix: true })}
								{!inquiry.item_id && inquiry.source !== 'item' && (
									<span> · via storefront</span>
								)}
							</p>
					</div>
				</div>
				</div>
			</div>




			{/* Reply history (own bubbles) */}
			{replies.length > 0 && (
				<div className="shrink-0 border-t border-white/[0.05] px-4 py-3 space-y-2 overflow-y-auto max-h-[40%]">
					{replies.map((r) => (
						<div key={r.id} className="flex justify-end">
							<div className="max-w-[82%] rounded-2xl bg-active-gold/15 border border-active-gold/25 px-4 py-2.5">
								<p className="text-[14px] text-foreground leading-snug whitespace-pre-wrap break-words">
									{r.body}
								</p>
								{r.attachments && r.attachments.length > 0 && (
									<div className="mt-2 flex flex-wrap gap-2">
										{r.attachments.map((path) => (
											<ReplyAttachment key={path} path={path} />
										))}
									</div>
								)}
								<p className="text-[10.5px] text-active-gold/80 mt-1.5 flex items-center gap-1.5">
									<span>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
									{r.email_sent_at ? (
										<>· <Check className="h-3 w-3" /> Emailed</>
									) : (
										<>· pending</>
									)}
								</p>
							</div>
						</div>
					))}
				</div>
			)}

			{/* Footer — native reply composer + quick chips */}
			<div className="shrink-0 border-t border-white/[0.05] px-3 py-2.5 space-y-2 bg-black/20">
				<QuickReplyRow
					onPick={(body) => {
						setDraft((d) => (d ? `${d}\n\n${body}` : body));
						focusComposer();
					}}
					itemTitle={(listing as any)?.title || (listing as any)?.name}
					offer={offer}
				/>
				<ChatComposer
					disabled={sendReply.isPending}
					placeholder={`Reply to ${inquiry.guest_name || inquiry.guest_email}…`}
					allowAttachments
					bucket="inquiry-attachments"
					pathPrefix={inquiry.id}
					onSend={async ({ body, attachmentPaths }) => {
						if (!body.trim()) return;
						sendReply.mutate(
							{ inquiryId: inquiry.id, inquiryTable: inquiry._table, body, attachmentPaths },
							{
								onSuccess: (res) => {
									setDraft('');
									toast.success(res?.emailQueued === false ? 'Reply saved (email pending)' : 'Reply sent');
								},
								onError: (e: any) => toast.error(e?.message || 'Could not send reply'),
							}
						);
					}}
				/>
			</div>


			<AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{confirm === 'block' && `Block ${inquiry.guest_email}?`}
							{confirm === 'report' && 'Report this inquiry as spam?'}
							{confirm === 'delete' && 'Delete this thread?'}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{confirm === 'block' &&
								'Future inquiries from this email will be silently dropped, and this thread will be removed from your inbox.'}
							{confirm === 'report' &&
								'CardSimp will review this message. It will also be marked as done.'}
							{confirm === 'delete' &&
								'This permanently removes the thread from your inbox. The sender will not be notified.'}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							disabled={confirmBusy}
							className={
								confirm === 'block'
									? ''
									: 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
							}
							onClick={(e) => {
								e.preventDefault();
								const action = confirm;
								setConfirm(null);
								if (action === 'block') onBlock();
								else if (action === 'report') onReport();
								else if (action === 'delete') onDelete();
							}}
						>
							{confirm === 'block' && (blockSender.isPending ? 'Blocking…' : 'Block')}
							{confirm === 'report' && (reportInquiry.isPending ? 'Reporting…' : 'Report')}
							{confirm === 'delete' && (deleteInquiry.isPending ? 'Deleting…' : 'Delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};

export default InquiryThread;

/**
 * Two-tap quick-reply chips. Selecting a chip inserts a pre-composed body
 * into the seller's reply composer (no mailto — keeps the thread native).
 */
function QuickReplyRow({
	onPick,
	itemTitle,
	offer,
}: {
	onPick: (body: string) => void;
	itemTitle?: string | null;
	offer?: string | null;
}) {
	const item = itemTitle ? `"${itemTitle}"` : 'this one';

	const chips: { label: string; body: string }[] = [
		{
			label: 'Still available',
			body: `Hey — thanks for reaching out. ${item} is still available. Let me know if you'd like to move forward.`,
		},
		{
			label: 'Sorry, sold',
			body: `Thanks for the interest — ${item} just sold. I'll keep you in mind if I get another.`,
		},
		offer
			? {
					label: 'Counter',
					body: `Thanks for the ${offer} offer on ${item}. I can't quite go that low — what's the most you can do?`,
				}
			: {
					label: "Let's meet",
					body: `Happy to meet up for ${item}. Which day/time works for you? I can also ship if that's easier.`,
				},
	];

	return (
		<div className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] pb-1 -mx-1 px-1">
			{chips.map((c) => (
				<button
					type="button"
					key={c.label}
					onClick={() => onPick(c.body)}
					className="shrink-0 inline-flex items-center h-8 px-3 rounded-full bg-white/[0.05] hover:bg-white/[0.08] active:bg-white/[0.1] text-[12px] font-medium text-foreground/85 border border-white/[0.06] transition-colors"
				>
					{c.label}
				</button>
			))}
		</div>
	);
}

